import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    // GreenFin: typing changes the query, so a highlighted result no longer applies. Clear it at once rather
    // than when the 200ms-debounced search runs, or Enter right after typing would follow the stale result.
    this.refs.searchInput.addEventListener(
      'input',
      () => {
        this.#currentIndex = -1;
      },
      { signal }
    );

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });
      // GreenFin: the combobox's popup is only on screen while the search dialog is open (aria-expanded).
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handlePopupOpen, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    this.#isPopupVisible = !dialog;
    this.#syncExpanded();

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#isPopupVisible = false;
    this.#syncExpanded();
    this.#resetSearch();
  };

  /** GreenFin: whether the results popup is on screen (always, outside a dialog). */
  #isPopupVisible = false;

  #handlePopupOpen = () => {
    this.#isPopupVisible = true;
    this.#syncExpanded();
    // GreenFin: the dialog opens in the click's task (open-immediately) and dispatches this synchronously, so
    // the input has focus before the next keystroke, whichever element a browser's dialog focusing picked.
    this.refs.searchInput.focus();
  };

  /**
   * GreenFin: ARIA 1.2 combobox state. The input is expanded while its popup is on screen and holds
   * options (results, suggestions or the empty-state products); a query with no options collapses it.
   */
  #syncExpanded() {
    const expanded = this.#isPopupVisible && this.#allResultsItems.length > 0;
    this.refs.searchInput.setAttribute('aria-expanded', String(expanded));
    this.#syncControls(this.#currentItem);
  }

  /**
   * GreenFin: ARIA 1.2 says the combobox's aria-controls names its popup, here a listbox (aria-haspopup). The
   * results are several listboxes (suggestions, products, collections, ...) in a group that also holds
   * headings and buttons, so it names the listbox holding the highlighted option, else the first one. With no
   * listbox at all the popup is collapsed (aria-expanded false) and the value is left as it was.
   * @param {HTMLElement | undefined} activeItem - The highlighted option, if any.
   */
  #syncControls(activeItem) {
    const listbox =
      activeItem?.closest('[role="listbox"][id]') ??
      this.refs.predictiveSearchResults.querySelector('[role="listbox"][id]');
    if (listbox) this.refs.searchInput.setAttribute('aria-controls', listbox.id);
  }

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  /**
   * GreenFin: loads the recently viewed products into the empty state without touching the input. Horizon
   * ran the full (100ms-debounced) reset here, which also cleared the input, so on the first open with
   * recently viewed products, characters typed straight after opening the search were wiped. If the shopper
   * is already typing, their query's results own the popup; the empty state is rendered at the next reset
   * (clearing the field or closing the dialog).
   */
  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    if (this.refs.searchInput.value) return;
    this.#emptyStateLoaded = true;
    this.#renderEmptyState();
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    const { searchInput } = this.refs;

    if (!this.#allResultsItems?.length) {
      // GreenFin: nothing to point at (e.g. the results were just replaced), so no active descendant.
      searchInput.removeAttribute('aria-activedescendant');
      return;
    }

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        // GreenFin: options carry ids from the markup; this only covers markup without one.
        if (!item.id) item.id = `predictive-search-option-${itemIndex}`;
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    // GreenFin: ARIA 1.2 combobox. Focus stays in the input; it points at the highlighted option.
    if (activeItem) {
      // Carousel cards (collections, pages) start aria-hidden off screen; the one highlighted is scrolled
      // into view below and must be exposed now, or the active option would not be announced.
      if (activeItem.getAttribute('aria-hidden') === 'true') activeItem.setAttribute('aria-hidden', 'false');
      searchInput.setAttribute('aria-activedescendant', activeItem.id);
    } else {
      searchInput.removeAttribute('aria-activedescendant');
    }
    this.#syncControls(activeItem ?? undefined);

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    // Horizontal arrows fall through to native text-caret movement, which the
    // browser already mirrors in RTL.
    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        // GreenFin: Enter follows a result only when the shopper has arrowed/tabbed to it (standard combobox
        // behaviour); otherwise it opens the full results page. Horizon also jumped straight to the result
        // whenever exactly one matched (data-single-result-url), so "monte" + Enter opened that product
        // instead of /search?q=monte. That shortcut and its marker in predictive-search.liquid are removed.
        // GreenFin: an Enter that confirms an IME composition isn't a search (Safari reports keyCode 229
        // with isComposing false for that Enter).
        if (event.isComposing || event.keyCode === 229) break;

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          // GreenFin: exactly one navigation. Horizon set location.href here without preventDefault, so the
          // form's native Enter submit raced it. Submit the search form itself (action routes.search_url, with
          // q and options[prefix]=last, as with no results, where this handler returns early and the native
          // submit runs); location.href only stands in when the input has no form.
          event.preventDefault();
          const { form } = this.refs.searchInput;
          if (form) {
            form.requestSubmit();
          } else {
            const searchUrl = new URL(Theme.routes.search_url, location.origin);
            searchUrl.searchParams.set('q', this.refs.searchInput.value);
            window.location.href = searchUrl.toString();
          }
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
        this.#afterResultsRender();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    const deferredPromise = SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm,
        },
        promise: deferredPromise.promise,
      })
    );

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) {
          deferredPromise.resolve({ totalCount: 0 });
          return;
        }

        if (abortController.signal.aborted) {
          deferredPromise.reject(new Error('Fetch aborted by user'));
          return;
        }

        morph(predictiveSearchResults, resultsMarkup);
        this.#afterResultsRender();

        this.#resetScrollPositions();

        // Count all result items (products, collections, pages, articles, queries)
        const resultCount = predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"]').length;
        deferredPromise.resolve({ totalCount: resultCount });
      })
      .catch((error) => {
        deferredPromise.reject(error);
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { searchInput } = this.refs;

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    await this.#renderEmptyState();
  };

  /**
   * GreenFin: the empty-state part of Horizon's #resetSearch (split out so loading it never clears the input).
   */
  async #renderEmptyState() {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;
    // GreenFin: typed while the empty state was loading; that query's search (still debouncing) renders next.
    if (searchInput.value.trim()) return;

    // GreenFin: recently viewed items are prepended here, so an index highlighted on the static empty
    // state would now point at a different product; drop it (#afterResultsRender clears activedescendant).
    this.#currentIndex = -1;
    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#afterResultsRender();
    this.#resetScrollPositions();
  }

  /**
   * GreenFin: after the popup's options change, drop an active descendant that is no longer highlighted
   * (a rendered option is never highlighted) and update aria-expanded.
   */
  #afterResultsRender() {
    const { searchInput } = this.refs;
    if (this.#currentIndex < 0) searchInput.removeAttribute('aria-activedescendant');
    this.#syncExpanded();
  }
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
